import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Loader2, ArrowRight, BookMarked, MessageCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { handsAiPlans, handsAiPlanDelete } from "@/lib/handsApi";

/**
 * Saved plans — a list of past Jixxeo A.I conversations. Every assistant
 * reply is auto-saved as part of a plan (session).
 */
export default function HandsPlans() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/helpinghands/plans"); return; }
    handsAiPlans()
      .then((r) => setPlans(r.plans || []))
      .catch(() => toast.error("Couldn't load your plans"))
      .finally(() => setBusy(false));
  }, [user, loading, nav]);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try {
      await handsAiPlanDelete(p.id);
      setPlans((list) => list.filter((x) => x.id !== p.id));
      toast.success("Plan deleted");
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div data-testid="hands-plans" className="pb-6 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#22C55E]/15 text-[#22C55E] flex items-center justify-center">
          <BookMarked className="w-4 h-4" fill="currentColor" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Auto-saved from Jixxeo A.I</div>
          <div className="font-black text-2xl">My plans</div>
        </div>
        <Link to="/helpinghands/ask" data-testid="plans-new-btn"
          className="inline-flex items-center gap-1 bg-[#22C55E] text-[#0A1512] font-black text-xs uppercase tracking-wide px-4 py-2 rounded-full">
          <Plus className="w-3 h-3" /> New chat
        </Link>
      </div>

      {busy ? (
        <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : plans.length === 0 ? (
        <div data-testid="plans-empty" className="rounded-3xl bg-gradient-to-br from-[#22C55E]/15 via-[#0F2118] to-[#0A1512] border border-[#22C55E]/30 p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#22C55E] text-[#0A1512] flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7" fill="currentColor" />
          </div>
          <div className="font-black text-xl">No plans yet</div>
          <div className="text-sm text-white/60 mt-1">Ask Jixxeo A.I a question about your money and we'll save the reply as a plan you can come back to.</div>
          <Link to="/helpinghands/ask" data-testid="plans-empty-cta"
            className="mt-5 inline-flex items-center gap-1 bg-[#22C55E] text-[#0A1512] font-black text-xs uppercase tracking-wide px-5 py-2.5 rounded-full">
            Start your first chat <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="plans-list">
          {plans.map((p) => (
            <li key={p.id} data-testid={`plan-${p.id}`} className="bg-[#0F2118] rounded-2xl border border-[#1D3A2E] hover:border-[#22C55E]/50 transition-colors">
              <Link to={`/helpinghands/ask/${p.id}`} className="flex gap-3 p-4">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-[#22C55E]/15 text-[#22C55E] flex items-center justify-center">
                  <Sparkles className="w-4 h-4" fill="currentColor" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-white truncate">{p.title || "Untitled plan"}</div>
                  <div className="text-xs text-white/60 line-clamp-2 mt-0.5">{p.last_reply_preview || "Tap to resume the conversation."}</div>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/40 uppercase tracking-widest">
                    <MessageCircle className="w-2.5 h-2.5" /> {p.message_count || 0} messages
                    <span>·</span>
                    <span>{new Date(p.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <button data-testid={`plan-del-${p.id}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(p); }}
                  className="self-start p-2 rounded-full text-white/40 hover:text-[#F87171] hover:bg-[#F87171]/10">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
